import { motion, useReducedMotion, useScroll, useTransform } from 'motion/react';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { BigLink } from '@/components/BigLink';
import { ClipImage } from '@/components/ClipImage';
import { Magnetic } from '@/components/Magnetic';
import { Meta } from '@/components/Meta';
import { Reveal, RevealWords } from '@/components/Reveal';
import { SplitReveal } from '@/components/SplitReveal';
import { projects } from '@/data/projects';
import { studio } from '@/data/studio';
import { useLang } from '@/hooks/useLang';

const HERO = '/images/residenze/1e3fb2_98e68741c987489bad5f0d5f673f49e2.jpg';

function Hero() {
  const ref = useRef<HTMLDivElement>(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] });
  const y = useTransform(scrollYProgress, [0, 1], ['0%', '22%']);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const { t } = useTranslation();

  return (
    <section ref={ref} className="relative h-[100svh] w-full overflow-hidden">
      <motion.img
        src={HERO}
        alt=""
        fetchPriority="high"
        style={reduce ? undefined : { y, scale }}
        className="absolute inset-0 h-full w-full object-cover"
      />
      {/* mood tint + strong bottom scrim so the white headline always reads */}
      <div className="pointer-events-none absolute inset-0 bg-ink/25" />
      <div className="pointer-events-none absolute inset-0 bg-linear-to-t from-ink/95 via-ink/55 to-transparent" />
      {/* top scrim: keeps the navbar legible */}
      <div className="pointer-events-none absolute inset-x-0 top-0 h-48 bg-linear-to-b from-ink/60 to-transparent" />

      <div className="u-container relative flex h-full flex-col justify-end pb-[12vh] text-white">
        <p className="mb-7 overflow-hidden text-sm uppercase tracking-[0.28em] text-white/90 md:text-base">
          <motion.span
            className="inline-block"
            initial={{ y: '120%' }}
            animate={{ y: '0%' }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
          >
            {t('home.heroEyebrow')}
          </motion.span>
        </p>
        <h1 className="max-w-6xl font-display text-[15vw] font-medium leading-[0.9] tracking-tight md:text-[9vw]">
          <RevealWords text={t('home.heroTitle')} delay={0.35} />
        </h1>
        <Reveal delay={1} className="mt-9 max-w-xl text-xl leading-relaxed text-white/90 md:text-2xl">
          {t('home.heroSub')}
        </Reveal>
      </div>

      <motion.div
        aria-hidden
        className="absolute bottom-8 left-1/2 flex -translate-x-1/2 flex-col items-center gap-2 text-white/80"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4 }}
      >
        <span className="text-xs uppercase tracking-[0.3em]">Scroll</span>
        <motion.span
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Number.POSITIVE_INFINITY, ease: 'easeInOut' }}
          className="text-3xl leading-none"
        >
          ↓
        </motion.span>
      </motion.div>
    </section>
  );
}

function Statement() {
  const { t } = useTranslation();
  return (
    <section className="u-container py-[14vh] md:py-[18vh]">
      <p className="eyebrow mb-10 text-brass">{t('home.statementEyebrow')}</p>
      <Reveal>
        <p className="max-w-5xl font-display text-4xl leading-[1.22] md:text-6xl md:leading-[1.16]">
          {t('home.statement')}
        </p>
      </Reveal>
    </section>
  );
}

/** Alternating full-bleed cinematic project rows. */
function FeaturedProjects() {
  const { t } = useTranslation();
  const lang = useLang();
  const featured = projects.slice(0, 3);

  return (
    <section className="space-y-[10vh] py-[6vh]">
      <div className="u-container flex items-end justify-between">
        <h2 className="font-display text-4xl md:text-6xl">{t('home.selectedWork')}</h2>
        <Link to="/lavori" className="link-line eyebrow hidden text-stone md:block">
          {t('home.allProjects')}
        </Link>
      </div>

      {featured.map((p, i) => {
        const title = lang === 'en' && p.titleEn ? p.titleEn : p.title;
        const summary = lang === 'en' && p.summaryEn ? p.summaryEn : p.summary;
        const flip = i % 2 === 1;
        return (
          <article
            key={p.slug}
            className="u-container grid items-center gap-8 md:grid-cols-12 md:gap-12"
          >
            <Link
              to={`/lavori/${p.slug}`}
              className={flip ? 'md:order-2 md:col-span-7' : 'md:col-span-7'}
            >
              <ClipImage
                src={p.cover}
                alt={title}
                className="aspect-4/3 w-full"
                imgClassName="transition-transform duration-[1200ms] ease-[cubic-bezier(0.16,1,0.3,1)] hover:scale-[1.03]"
              />
            </Link>
            <div className={flip ? 'md:order-1 md:col-span-5' : 'md:col-span-5'}>
              <Reveal>
                <p className="eyebrow text-stone">
                  <span className="text-brass">{String(i + 1).padStart(2, '0')}</span> — {p.location}
                </p>
                <h3 className="mt-4 font-display text-4xl md:text-5xl">{title}</h3>
                <p className="mt-5 max-w-md text-lg leading-relaxed text-ink-soft">{summary}</p>
                <Link to={`/lavori/${p.slug}`} className="link-line eyebrow mt-7 inline-block">
                  {t('home.viewProject')}
                </Link>
              </Reveal>
            </div>
          </article>
        );
      })}
    </section>
  );
}

function ProductTeaser() {
  const { t } = useTranslation();
  return (
    <section className="relative my-[10vh] grid items-center gap-0 md:grid-cols-2">
      <div className="relative aspect-square w-full overflow-hidden bg-paper-deep md:aspect-auto md:h-[90vh]">
        <motion.img
          src={studio.saturno}
          alt="SATURNO"
          loading="lazy"
          decoding="async"
          initial={{ opacity: 0, scale: 1.05 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
          style={{ objectPosition: 'center' }}
          className="absolute inset-0 h-full w-full object-cover"
        />
      </div>
      <div className="u-container py-16 md:py-0">
        <Reveal>
          <p className="eyebrow text-brass">{t('home.productEyebrow')}</p>
          <h2 className="mt-5 font-display text-5xl italic md:text-7xl">SATURNO</h2>
          <p className="mt-6 max-w-md text-lg leading-relaxed text-ink-soft">{t('home.productText')}</p>
          <Magnetic className="mt-9 inline-block">
            <Link
              to="/design"
              className="inline-flex items-center gap-3 rounded-full border border-ink px-7 py-3 text-xs uppercase tracking-[0.2em] transition-colors hover:bg-ink hover:text-paper"
            >
              {t('home.productCta')} <span aria-hidden>→</span>
            </Link>
          </Magnetic>
        </Reveal>
      </div>
    </section>
  );
}

function StudioTeaser() {
  const { t } = useTranslation();
  return (
    <section className="u-container grid items-center gap-12 py-[12vh] md:grid-cols-12">
      <div className="md:col-span-5">
        <ClipImage src={studio.portrait} alt={studio.name} className="aspect-3/4 w-full" />
      </div>
      <div className="md:col-span-6 md:col-start-7">
        <Reveal>
          <p className="eyebrow text-stone">{t('home.studioEyebrow')}</p>
          <p className="mt-6 font-display text-3xl leading-snug md:text-4xl">{t('home.studioQuote')}</p>
          <Link to="/studio" className="link-line eyebrow mt-8 inline-block">
            {t('home.studioCta')}
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

function ContactCta() {
  const { t } = useTranslation();
  return (
    <section className="u-container py-[18vh] text-center">
      <Reveal className="flex flex-col items-center">
        <p className="eyebrow mb-8 text-stone">{t('home.contactEyebrow')}</p>
        <BigLink to="/contatti" className="text-[15vw] hover:opacity-70 md:text-[9.5vw]">
          {t('home.contactTitle')}
        </BigLink>
      </Reveal>
    </section>
  );
}

export function HomePage() {
  const { t } = useTranslation();
  return (
    <>
      <Meta title={t('meta.homeTitle')} description={t('meta.homeDesc')} />
      <Hero />
      <Statement />
      <SplitReveal
        src="/images/residenze/1e3fb2_0e6631bb18834e85bb687e44627edb68.jpg"
        alt="Interior — Studio Atelier"
        eyebrow={t('split.eyebrow')}
        lines={[t('split.line1'), t('split.line2'), t('split.line3')]}
        caption={t('split.caption')}
      />
      <FeaturedProjects />
      <ProductTeaser />
      <StudioTeaser />
      <ContactCta />
    </>
  );
}
